import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '../../components/Modal';
import { useCreateContact, useUpdateContact, useSetCustomFieldValues, useContactSources } from '../hooks';
import { companiesApi } from '../api';
import { CustomFieldsForm } from '../CustomFieldsForm';
import type {
  Contact,
  ContactType,
  EntityStatus,
  Company,
  CustomFieldValue,
} from '../types';
import { ENTITY_STATUS_LABELS } from '../types';

/** Vorbelegung aus externen Quellen (z.B. Outlook-Kontakt) */
export interface ContactPrefillData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  position?: string;
  companyName?: string;
}

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: Contact | null;
  onSuccess: () => void;
  /** Vorausgewähltes Unternehmen (z.B. aus der Unternehmens-Detailseite) */
  preselectedCompany?: { id: string; name: string } | null;
  /** Vorbelegung aus externen Quellen (z.B. Outlook) */
  prefill?: ContactPrefillData | null;
}

type FormTab = 'general' | 'details';

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text)',
  marginBottom: '0.25rem',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 0.75rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.9375rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.75rem',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  margin: '1.5rem 0 0.75rem',
  paddingBottom: '0.5rem',
  borderBottom: '1px solid var(--color-border)',
  display: 'block',
};

export function ContactFormModal({
  isOpen,
  onClose,
  contact,
  onSuccess,
  preselectedCompany,
  prefill,
}: ContactFormModalProps) {
  const isEditMode = !!contact;
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const setCustomFieldValues = useSetCustomFieldValues();
  const mutation = isEditMode ? updateMutation : createMutation;
  const { data: contactSourcesData } = useContactSources();
  const contactSources = contactSourcesData?.data ?? [];

  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState<FormTab>('general');

  const customFieldValuesRef = useRef<
    Record<string, string | number | boolean | string[] | null>
  >({});
  const customFields: CustomFieldValue[] = contact?.customFields ?? [];
  const handleCustomFieldsChange = useCallback(
    (values: Record<string, string | number | boolean | string[] | null>) => {
      customFieldValuesRef.current = values;
    },
    [],
  );

  // type is preserved internally for existing ORGANIZATION contacts,
  // but new contacts always default to PERSON (no UI selector)
  const [type, setType] = useState<ContactType>('PERSON');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [website, setWebsite] = useState('');
  const [street, setStreet] = useState('');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('DE');
  const [notes, setNotes] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [position, setPosition] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [xingUrl, setXingUrl] = useState('');
  const [preferredContact, setPreferredContact] = useState('');
  const [decisionLevel, setDecisionLevel] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('');
  const [timezone, setTimezone] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [birthday, setBirthday] = useState('');
  const [source, setSource] = useState('');
  const [department, setDepartment] = useState('');
  const [status, setStatus] = useState<EntityStatus>('ACTIVE');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // When a company is linked, the address can be inherited from it
  const [useCompanyAddress, setUseCompanyAddress] = useState(false);

  // Company autocomplete
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);
  const companySearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Click-outside for company dropdown
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        companyRef.current &&
        !companyRef.current.contains(e.target as Node)
      ) {
        setShowCompanyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Company search (debounced)
  useEffect(() => {
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    if (!companySearch || companySearch.length < 2) {
      setCompanyResults([]);
      return;
    }
    companySearchTimeout.current = setTimeout(async () => {
      try {
        const res = await companiesApi.list({
          search: companySearch,
          pageSize: 8,
        });
        setCompanyResults(res.data);
        setShowCompanyDropdown(true);
      } catch {
        setCompanyResults([]);
      }
    }, 300);
    return () => {
      if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    };
  }, [companySearch]);

  // Initialise form when drawer opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setActiveTab('general');
      if (contact) {
        setType(contact.type);
        setFirstName(contact.firstName ?? '');
        setLastName(contact.lastName ?? '');
        setCompanyName(contact.companyName ?? '');
        setEmail(contact.email ?? '');
        setPhone(contact.phone ?? '');
        setMobile(contact.mobile ?? '');
        setWebsite(contact.website ?? '');
        setStreet(contact.street ?? '');
        setZip(contact.zip ?? '');
        setCity(contact.city ?? '');
        setCountry(contact.country ?? 'DE');
        setNotes(contact.notes ?? '');
        setTagsInput((contact.tags ?? []).join(', '));
        setPosition(contact.position ?? '');
        setLinkedinUrl(contact.linkedinUrl ?? '');
        setXingUrl(contact.xingUrl ?? '');
        setPreferredContact(contact.preferredContact ?? '');
        setDecisionLevel(contact.decisionLevel ?? '');
        setPreferredLanguage(contact.preferredLanguage ?? '');
        setTimezone(contact.timezone ?? '');
        setNextStep(contact.nextStep ?? '');
        setBirthday(
          contact.birthday ? contact.birthday.split('T')[0] : '',
        );
        setSource(contact.source ?? '');
        setDepartment(contact.department ?? '');
        setStatus(contact.status ?? 'ACTIVE');
        if (contact.company) {
          setSelectedCompany({
            id: contact.company.id,
            name: contact.company.name,
          });
          setCompanySearch(contact.company.name);
          // Default: use company address when contact has no own address
          setUseCompanyAddress(!contact.street);
        } else {
          setSelectedCompany(null);
          setCompanySearch('');
          setUseCompanyAddress(false);
        }
      } else {
        setType('PERSON');
        setFirstName('');
        setLastName('');
        setCompanyName('');
        setEmail('');
        setPhone('');
        setMobile('');
        setWebsite('');
        setStreet('');
        setZip('');
        setCity('');
        setCountry('DE');
        setNotes('');
        setTagsInput('');
        setPosition('');
        setLinkedinUrl('');
        setXingUrl('');
        setPreferredContact('');
        setDecisionLevel('');
        setPreferredLanguage('');
        setTimezone('');
        setNextStep('');
        setBirthday('');
        setSource('');
        setDepartment('');
        setStatus('ACTIVE');
        setFieldErrors({});
        if (preselectedCompany) {
          setSelectedCompany({ id: preselectedCompany.id, name: preselectedCompany.name });
          setCompanySearch(preselectedCompany.name);
          setUseCompanyAddress(true);
        } else {
          setSelectedCompany(null);
          setCompanySearch('');
          setUseCompanyAddress(true); // default: inherit from company once one is selected
        }
        // Prefill aus externer Quelle (z.B. Outlook)
        if (prefill) {
          if (prefill.firstName) setFirstName(prefill.firstName);
          if (prefill.lastName) setLastName(prefill.lastName);
          if (prefill.email) setEmail(prefill.email);
          if (prefill.phone) setPhone(prefill.phone);
          if (prefill.mobile) setMobile(prefill.mobile);
          if (prefill.position) setPosition(prefill.position);
          if (prefill.companyName) {
            setCompanySearch(prefill.companyName);
          }
        }
      }
      setCompanyResults([]);
      setShowCompanyDropdown(false);
    }
  }, [isOpen, contact, preselectedCompany, prefill]);

  const validateField = (field: string, value: string) => {
    let msg = '';
    if (field === 'firstName' && type === 'PERSON' && !value.trim()) msg = 'Vorname ist erforderlich';
    if (field === 'lastName' && type === 'PERSON' && !value.trim()) msg = 'Nachname ist erforderlich';
    if (field === 'companyName' && type === 'ORGANIZATION' && !value.trim()) msg = 'Firmenname ist erforderlich';
    if (field === 'email' && !value.trim()) msg = 'E-Mail ist erforderlich';
    setFieldErrors((prev) => ({ ...prev, [field]: msg }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Cross-tab validation
    const errors: Record<string, string> = {};
    if (type === 'PERSON') {
      if (!firstName.trim()) { errors.firstName = 'Vorname ist erforderlich'; }
      if (!lastName.trim()) { errors.lastName = 'Nachname ist erforderlich'; }
    }
    if (type === 'ORGANIZATION' && !companyName.trim()) {
      errors.companyName = 'Firmenname ist erforderlich';
    }
    if (!email.trim()) { errors.email = 'E-Mail ist erforderlich'; }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Switch to tab with errors
      if (errors.firstName || errors.lastName || errors.email || errors.companyName) {
        setActiveTab('general');
      }
      return;
    }

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    // Only include address when not inheriting from company
    const addressPayload =
      !selectedCompany || !useCompanyAddress
        ? {
            ...(street ? { street } : {}),
            ...(zip ? { zip } : {}),
            ...(city ? { city } : {}),
            country,
          }
        : {};

    const payload = {
      type,
      ...(type === 'PERSON' ? { firstName, lastName } : { companyName }),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(mobile ? { mobile } : {}),
      ...(website ? { website } : {}),
      ...(linkedinUrl ? { linkedinUrl } : {}),
      ...(xingUrl ? { xingUrl } : {}),
      ...(preferredContact ? { preferredContact: preferredContact as 'EMAIL' | 'PHONE' | 'MOBILE' } : {}),
      ...(decisionLevel ? { decisionLevel: decisionLevel as 'C_LEVEL' | 'DIRECTOR' | 'MANAGER' | 'OPERATIONAL' | 'OTHER' } : {}),
      ...(preferredLanguage ? { preferredLanguage } : {}),
      ...(timezone ? { timezone } : {}),
      ...(nextStep ? { nextStep } : {}),
      ...(birthday ? { birthday: new Date(birthday).toISOString() } : {}),
      ...(source ? { source } : {}),
      ...(department ? { department } : {}),
      status,
      ...addressPayload,
      ...(notes ? { notes } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...(selectedCompany ? { companyId: selectedCompany.id } : {}),
      ...(position ? { position } : {}),
    };

    const saveCustomFields = (entityId: string) => {
      const vals = customFieldValuesRef.current;
      const entries = Object.entries(vals);
      if (entries.length === 0) return;
      setCustomFieldValues.mutate({
        entityId,
        data: {
          values: entries.map(([fieldDefId, value]) => ({
            fieldDefId,
            value,
          })),
        },
      });
    };

    if (isEditMode && contact) {
      updateMutation.mutate(
        { id: contact.id, data: payload },
        {
          onSuccess: () => {
            saveCustomFields(contact.id);
            setSuccessMsg('Kontakt gespeichert');
            setTimeout(() => { setSuccessMsg(''); onSuccess(); }, 800);
          },
          onError: (err: unknown) => {
            const msg =
              (
                err as {
                  response?: { data?: { error?: { message?: string } } };
                }
              )?.response?.data?.error?.message ?? 'Fehler beim Speichern';
            setError(msg);
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res?.data?.id) saveCustomFields(res.data.id);
          setSuccessMsg('Kontakt erstellt');
          setTimeout(() => { setSuccessMsg(''); onSuccess(); }, 800);
        },
        onError: (err: unknown) => {
          const msg =
            (
              err as {
                response?: { data?: { error?: { message?: string } } };
              }
            )?.response?.data?.error?.message ?? 'Fehler beim Anlegen';
          setError(msg);
        },
      });
    }
  };

  const tabBtnStyle = (tab: FormTab): React.CSSProperties => ({
    padding: '0.5rem 1.25rem',
    background: 'none',
    border: 'none',
    borderBottom: `2px solid ${activeTab === tab ? 'var(--color-primary)' : 'transparent'}`,
    cursor: 'pointer',
    fontSize: '0.9375rem',
    fontWeight: activeTab === tab ? 600 : 400,
    color:
      activeTab === tab
        ? 'var(--color-primary)'
        : 'var(--color-text-secondary)',
    marginBottom: '-1px',
    transition: 'color 0.15s, border-color 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
      maxWidth="720px"
    >
      {/* Success toast */}
      {successMsg && (
        <div
          style={{
            padding: '0.75rem',
            background: 'var(--color-success-bg)',
            border: '1px solid var(--color-success-border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-success-text)',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: '1rem',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '1.125rem' }}>✓</span> {successMsg}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: 'var(--color-error-bg)',
            border: '1px solid var(--color-error-border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-error)',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Tab navigation */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--color-border)',
          marginBottom: '1.5rem',
        }}
      >
        <button
          type="button"
          style={tabBtnStyle('general')}
          onClick={() => setActiveTab('general')}
        >
          Stammdaten &amp; Firma
        </button>
        <button
          type="button"
          style={tabBtnStyle('details')}
          onClick={() => setActiveTab('details')}
        >
          Kontakt &amp; Social
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── TAB 1: Stammdaten & Firma ─────────────────────────── */}
        {activeTab === 'general' && (
          <>
            {/* Name */}
            {type === 'PERSON' ? (
              <div style={{ ...rowStyle, marginBottom: '1rem' }}>
                <div>
                  <label style={labelStyle}>Vorname <span style={{ color: 'var(--color-error)' }}>*</span></label>
                  <input
                    style={{ ...inputStyle, borderColor: fieldErrors.firstName ? 'var(--color-error)' : 'var(--color-border)' }}
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); if (fieldErrors.firstName) setFieldErrors((p) => ({ ...p, firstName: '' })); }}
                    onBlur={(e) => validateField('firstName', e.target.value)}
                    placeholder="Max"
                  />
                  {fieldErrors.firstName && <span style={{ color: 'var(--color-error)', fontSize: '0.75rem' }}>{fieldErrors.firstName}</span>}
                </div>
                <div>
                  <label style={labelStyle}>Nachname <span style={{ color: 'var(--color-error)' }}>*</span></label>
                  <input
                    style={{ ...inputStyle, borderColor: fieldErrors.lastName ? 'var(--color-error)' : 'var(--color-border)' }}
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); if (fieldErrors.lastName) setFieldErrors((p) => ({ ...p, lastName: '' })); }}
                    onBlur={(e) => validateField('lastName', e.target.value)}
                    placeholder="Mustermann"
                  />
                  {fieldErrors.lastName && <span style={{ color: 'var(--color-error)', fontSize: '0.75rem' }}>{fieldErrors.lastName}</span>}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Firmenname</label>
                <input
                  style={{ ...inputStyle, borderColor: fieldErrors.companyName ? 'var(--color-error)' : 'var(--color-border)' }}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onBlur={(e) => validateField('companyName', e.target.value)}
                  placeholder="Muster GmbH"
                />
                {fieldErrors.companyName && <span style={{ color: 'var(--color-error)', fontSize: '0.75rem' }}>{fieldErrors.companyName}</span>}
              </div>
            )}

            {/* Company autocomplete */}
            <div
              style={{ marginBottom: '1rem', position: 'relative' }}
              ref={companyRef}
            >
              <label style={labelStyle}>Unternehmen</label>
              <div style={{ position: 'relative' }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="6.5" cy="6.5" r="5" /><path d="M10.5 10.5l3 3" />
                </svg>
              <input
                style={{ ...inputStyle, paddingLeft: '2rem' }}
                value={companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  if (selectedCompany) {
                    setSelectedCompany(null);
                    setUseCompanyAddress(false);
                  }
                }}
                onFocus={() => {
                  if (companyResults.length > 0) setShowCompanyDropdown(true);
                }}
                placeholder="Unternehmen suchen…"
              />
              {selectedCompany && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanySearch('');
                    setUseCompanyAddress(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: 30,
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    fontSize: '1rem',
                  }}
                >
                  ×
                </button>
              )}
              {showCompanyDropdown && companyResults.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'var(--color-bg-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: 'var(--shadow-md)',
                    zIndex: 10,
                    maxHeight: 200,
                    overflowY: 'auto',
                  }}
                >
                  {companyResults.map((comp) => (
                    <div
                      key={comp.id}
                      onClick={() => {
                        setSelectedCompany({ id: comp.id, name: comp.name });
                        setCompanySearch(comp.name);
                        setShowCompanyDropdown(false);
                        setUseCompanyAddress(true); // default: inherit address
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        borderBottom: '1px solid var(--color-border)',
                      }}
                      onMouseEnter={(e) =>
                        (
                          (e.currentTarget as HTMLDivElement).style.background =
                            'var(--color-bg)'
                        )
                      }
                      onMouseLeave={(e) =>
                        (
                          (e.currentTarget as HTMLDivElement).style.background =
                            'transparent'
                        )
                      }
                    >
                      {comp.name}
                      {comp.industry && (
                        <span
                          style={{
                            color: 'var(--color-text-muted)',
                            marginLeft: '0.5rem',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {comp.industry}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </div>{/* end lupen wrapper */}
            </div>

            {/* Position + Abteilung (PERSON only) */}
            {type === 'PERSON' && (
              <div style={{ ...rowStyle, marginBottom: '1rem' }}>
                <div>
                  <label style={labelStyle}>Position</label>
                  <input
                    style={inputStyle}
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    placeholder="z.B. Geschäftsführer"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Abteilung</label>
                  <input
                    style={inputStyle}
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="z.B. Einkauf"
                  />
                </div>
              </div>
            )}

            {/* E-Mail + Telefon + Mobil (3 Spalten) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>E-Mail <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <input
                  type="email"
                  style={{ ...inputStyle, borderColor: fieldErrors.email ? 'var(--color-error)' : 'var(--color-border)' }}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: '' })); }}
                  onBlur={(e) => validateField('email', e.target.value)}
                  placeholder="mail@example.com"
                />
                {fieldErrors.email && <span style={{ color: 'var(--color-error)', fontSize: '0.75rem' }}>{fieldErrors.email}</span>}
              </div>
              <div>
                <label style={labelStyle}>Telefon</label>
                <input
                  style={inputStyle}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 …"
                />
              </div>
              <div>
                <label style={labelStyle}>Mobil</label>
                <input
                  style={inputStyle}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+49 …"
                />
              </div>
            </div>

          </>
        )}

        {/* ── TAB 2: Kontakt & Social ──────────────────────────── */}
        {activeTab === 'details' && (
          <>
            {/* Geburtsdatum + Quelle */}
            <div style={{ ...rowStyle, marginBottom: '1rem' }}>
              {type === 'PERSON' && (
                <div>
                  <label style={labelStyle}>Geburtsdatum</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </div>
              )}
              <div>
                <label style={labelStyle}>Quelle</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  <option value="">-- Keine --</option>
                  {contactSources.slice().sort((a, b) => a.name.localeCompare(b.name, 'de')).map((cs) => (
                    <option key={cs.id} value={cs.name}>
                      {cs.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Status</label>
              <select
                style={{ ...inputStyle, cursor: 'pointer' }}
                value={status}
                onChange={(e) => setStatus(e.target.value as EntityStatus)}
              >
                {Object.entries(ENTITY_STATUS_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Website */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Website</label>
              <input
                style={inputStyle}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://…"
              />
            </div>

            {/* ── Adresse ── */}
            <span style={sectionLabelStyle}>Adresse</span>

            {/* "Adresse vom Unternehmen übernehmen" */}
            {selectedCompany && (
              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--color-text)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={useCompanyAddress}
                    onChange={(e) => setUseCompanyAddress(e.target.checked)}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                  />
                  Adresse vom Unternehmen übernehmen
                </label>
                {useCompanyAddress && (
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-primary)',
                      marginTop: '0.5rem',
                      marginLeft: '1.5rem',
                      marginBottom: 0,
                      background: 'var(--color-info-bg)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-info-border)',
                    }}
                  >
                    ✓ Adresse wird automatisch aus „{selectedCompany.name}" übernommen.
                  </p>
                )}
              </div>
            )}

            {/* Address fields (hidden when using company address) */}
            {(!selectedCompany || !useCompanyAddress) && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Straße</label>
                  <input
                    style={inputStyle}
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={labelStyle}>PLZ</label>
                    <input
                      style={inputStyle}
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Stadt</label>
                    <input
                      style={inputStyle}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Land</label>
                  <input
                    style={inputStyle}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="DE"
                    maxLength={2}
                  />
                </div>
              </>
            )}

            {/* ── Erreichbarkeit & Profil ── */}
            <span style={sectionLabelStyle}>Erreichbarkeit &amp; Profil</span>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>LinkedIn</label>
              <input
                style={inputStyle}
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/…"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Xing-URL</label>
              <input
                style={inputStyle}
                value={xingUrl}
                onChange={(e) => setXingUrl(e.target.value)}
                placeholder="https://xing.com/profile/…"
              />
            </div>

            <div style={{ ...rowStyle, marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Bevorzugter Kontaktweg</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={preferredContact}
                  onChange={(e) => setPreferredContact(e.target.value)}
                >
                  <option value="">-- Keine Angabe --</option>
                  <option value="EMAIL">E-Mail</option>
                  <option value="PHONE">Telefon</option>
                  <option value="MOBILE">Mobil</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Bevorzugte Sprache</label>
                <input
                  style={inputStyle}
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                  placeholder="z.B. de, en"
                  maxLength={10}
                />
              </div>
            </div>

            <div style={{ ...rowStyle, marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Zeitzone</label>
                <input
                  style={inputStyle}
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="z.B. Europe/Berlin"
                  maxLength={50}
                />
              </div>
              <div>
                <label style={labelStyle}>Entscheider-Level</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={decisionLevel}
                  onChange={(e) => setDecisionLevel(e.target.value)}
                >
                  <option value="">-- Keine Angabe --</option>
                  <option value="C_LEVEL">C-Level</option>
                  <option value="DIRECTOR">Direktor/in</option>
                  <option value="MANAGER">Manager/in</option>
                  <option value="OPERATIONAL">Operativ</option>
                  <option value="OTHER">Sonstige</option>
                </select>
              </div>
            </div>

            {/* ── Nächster Schritt ── */}
            <span style={sectionLabelStyle}>Nächster Schritt</span>
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                placeholder="Nächste Aktion oder Follow-up..."
              />
            </div>

            {/* ── Notizen & Tags ── */}
            <span style={sectionLabelStyle}>Notizen &amp; Tags</span>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Notizen</label>
              <textarea
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Tags</label>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center',
                padding: '0.375rem 0.5rem', minHeight: 42,
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                background: 'var(--color-bg-card)',
              }}>
                {tagsInput.split(',').filter((t) => t.trim()).map((tag, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.1875rem 0.5rem', borderRadius: '999px',
                    background: '#dbeafe', color: 'var(--color-info-text)', fontSize: '0.8125rem', fontWeight: 500,
                  }}>
                    {tag.trim()}
                    <button type="button" onClick={() => {
                      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
                      tags.splice(i, 1);
                      setTagsInput(tags.join(', '));
                    }} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--color-info-text)', fontSize: '0.875rem', padding: 0, lineHeight: 1,
                    }}>×</button>
                  </span>
                ))}
                <input
                  style={{
                    flex: 1, minWidth: 80, border: 'none', outline: 'none',
                    background: 'transparent', fontSize: '0.875rem', padding: '0.25rem',
                    color: 'var(--color-text)',
                  }}
                  placeholder={tagsInput ? '' : 'Tag eingeben + Enter...'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val) {
                        const existing = tagsInput ? tagsInput + ', ' + val : val;
                        setTagsInput(existing);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value && tagsInput) {
                      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
                      tags.pop();
                      setTagsInput(tags.join(', '));
                    }
                  }}
                />
              </div>
            </div>

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <CustomFieldsForm
                fields={customFields}
                onChange={handleCustomFieldsChange}
              />
            )}
          </>
        )}
        {/* Buttons — sticky footer */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--color-bg-card)',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            padding: '1rem 1.5rem',
            zIndex: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: mutation.isPending ? 'wait' : 'pointer',
              opacity: mutation.isPending ? 0.7 : 1,
            }}
          >
            {mutation.isPending
              ? 'Speichern...'
              : isEditMode
                ? 'Speichern'
                : 'Anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
